import { Geometry } from '@turf/helpers';
import { timeZonesNames } from '@vvo/tzdb';
import axios from 'axios';
import { DateTime } from 'luxon';
import { capitalize } from 'vue';

import DatadogApi from '@/common/apis/DatadogApi';
import TableColumnTypes from '@/common/components/controls/table/TableColumnTypes';
import ImpressionVerifierVendorEnum from '@/common/enums/impression-verifier-vendor.enum';
import { Filter, IABCategory, Language } from '@/common/models';
import { convertRangesToDaypart, daypartToRanges, parseRanges } from '@/common/models/Dayparting';
import EntityProperty from '@/common/models/EntityProperty';
import Exception from '@/common/models/Exception';
import Location, { ILocationAPI } from '@/common/models/geo/Location';
import IABCategories from '@/common/models/iab-category/IABCategories';
import MediaType, { IMediaTypeAPI } from '@/common/models/misc-api-models/MediaType';
import Network, { INetworkAPI } from '@/common/models/misc-api-models/Network';
import {
  FilterCollection,
  FilterComparisonOperator,
  FilterGroup,
  FilterLogicalOperator,
  QueryParams,
} from '@/common/models/query-params';
import { Action } from '@/common/models/query-params/QueryParams';
import Site, { ISiteAPI } from '@/common/models/site/Site';
import UnitFacingDirectionOptions from '@/common/models/unit/UnitFacingDirectionOptions';
import UnitFallbackCreative, { IUnitFallbackCreativeAPI } from '@/common/models/unit/UnitFallbackCreative';
import UnitLanguage, { IUnitLanguageAPI } from '@/common/models/unit/UnitLanguage';
import { IUnitPlayerIntegrationAPI, UnitPlayerIntegration } from '@/common/models/unit/UnitPlayerIntegration';
import UnitProperty, { IUnitPropertyAPI } from '@/common/models/unit/UnitProperty';
import GlobalEventBus from '@/GlobalEventBus';
import GlobalEvents from '@/GlobalEvents';
import {
  areArraysEqual,
  DASH,
  FullDaypart,
  getDaypartDescription,
  getStore,
  GoogleMapsApiKey,
  InvalidDaypart,
  isValidDaypart,
} from '@/globals';
import { useLanguages, useMediaOwners, useMediaTypesFlatten, useNetworks } from '@/org-app/services/tanstack/queries';
import { peekQueryInScope, runQueryInScope } from '@/org-app/services/tanstack/queryUtils';
import orgAppStore from '@/org-app/store';
import rootStore from '@/store';
import {
  UPDATE_UNIT_AD_SERVER_AVAILABLITY,
  UPDATE_UNIT_ALLOWAUDIO,
  UPDATE_UNIT_ALLOWHTML,
  UPDATE_UNIT_ALLOWIMAGE,
  UPDATE_UNIT_ALLOWVIDEO,
  UPDATE_UNIT_ALLOWZIP,
  UPDATE_UNIT_BLOCKUNIQUEADVERTISERIDS,
  UPDATE_UNIT_DEAL_AVAILABILITY,
  UPDATE_UNIT_DESCRIPTION,
  UPDATE_UNIT_ENABLE_STRICT_FREQUENCY_CAPPING,
  UPDATE_UNIT_ENABLE_STRICT_IAB_BLOCK_CATEGORIES,
  UPDATE_UNIT_EXTERNALID,
  UPDATE_UNIT_FACING_DIRECTION,
  UPDATE_UNIT_FALLBACK_CREATIVES,
  UPDATE_UNIT_FLOORCPM,
  UPDATE_UNIT_IAB_BLOCK_CATEGORIES,
  UPDATE_UNIT_IMPRESSION_VERIFIER_VENDOR,
  UPDATE_UNIT_IMPRESSION_VERIFIER_VENDOR_EXTERNAL_ID,
  UPDATE_UNIT_ISACTIVE,
  UPDATE_UNIT_LATITUDE,
  UPDATE_UNIT_LONGITUDE,
  UPDATE_UNIT_MAX_SPOTLENGTH,
  UPDATE_UNIT_MEDIATYPEID,
  UPDATE_UNIT_MIN_SECONDS_BETWEEN_AD_DOMAIN_PLAYS,
  UPDATE_UNIT_MIN_SECONDS_BETWEEN_IAB_CATEGORY_PLAYS,
  UPDATE_UNIT_MIN_SPOTLENGTH,
  UPDATE_UNIT_NAME,
  UPDATE_UNIT_NETWORKID,
  UPDATE_UNIT_OPEN_EXCHANGE_AVAILABILITY,
  UPDATE_UNIT_OPERATINGHOURS,
  UPDATE_UNIT_PHYSICAL_UNIT_COUNT,
  UPDATE_UNIT_SCREENHEIGHT,
  UPDATE_UNIT_SCREENWIDTH,
  UPDATE_UNIT_SITEID,
  UPDATE_UNIT_SPOTLENGTH,
  UPDATE_UNIT_STORE_FRONT_AVIALABILTY,
  UPDATE_UNIT_TAGS,
  UPDATE_UNIT_TIMEZONE,
  UPDATE_UNIT_UNITLANGUAGES,
  UPDATE_UNIT_UUID,
} from '@/store/modules/models/mutation-types';
import { IPaginationResult } from '@/utils/ApiUtils';
import { getArrayDiff, getDiff } from '@/utils/Diff';
import Validatable, { IComplexValidationRule, IEntityChange, IExtraValidation } from '@/validator/Validatable';
import i18n from '@/vueI18n';

const type = 'Unit';

interface IUnitGeofenceAPI {
  created_on_utc: string;
  geo_json: Geometry;
  geofence_id: number;
  modified_on_utc: string;
}

export interface IUnitAPI {
  active: boolean;
  allow_audio: boolean;
  allow_html: boolean;
  allow_image: boolean;
  allow_video: boolean;
  allow_zip: boolean;
  app_domain_id: string;
  available_for_adserver: boolean;
  available_for_deals: boolean;
  available_for_open_exchange: boolean;
  available_for_storefront: boolean;
  blacklist_unique_advertiser_ids: number[];
  created_on_utc: string;
  description: string;
  enable_strict_iab_blacklisting: boolean;
  enable_strict_iab_frequency_capping: boolean;
  external_id: string;
  facing_direction: string;
  floor_cpm: number;
  geofence: IUnitGeofenceAPI;
  has_audience_data: boolean;
  iab_blacklist_categories: string[];
  image_uri: string;
  impression_verifier_vendor: string;
  impression_verifier_vendor_external_id: string;
  last_heard_from_seconds_ago: number;
  last_heard_from_utc: string;
  last_played_on_seconds_ago: number;
  last_played_on_utc: string;
  latitude: number;
  location: ILocationAPI;
  location_id: number;
  location_id_overridden: boolean;
  longitude: number;
  loop_length: number;
  max_spot_length: number;
  media_type: IMediaTypeAPI;
  mediatype_id: number;
  min_seconds_between_ad_domain_plays: number;
  min_seconds_between_iab_category_plays: number;
  min_spot_length: number;
  modified_on_utc: string;
  name: string;
  network: INetworkAPI;
  network_id: number;
  openrtb_uuid: string;
  operating_hours: string;
  owner_id: number;
  physical_screen_height_cm: number;
  physical_screen_width_cm: number;
  physical_unit_count: number;
  screen_height: number;
  screen_width: number;
  site: ISiteAPI;
  site_id: number;
  spot_length: number;
  tags: string[];
  timezone: string;
  unit_id: number;
  unit_languages: IUnitLanguageAPI[];
  unit_properties: IUnitPropertyAPI[];
  unitfallbackcreatives: IUnitFallbackCreativeAPI[];
  uuid: string;
  zip_code: string;
  serial_number?: string;
  unit_player_integration?: IUnitPlayerIntegrationAPI;
}

// Blacklist in BE is Block in FE same as Unit in BE and Screen in FE
export default class Unit extends Validatable {
  apiObject: IUnitAPI = null;
  cachedOriginalObject: Unit;

  static languagesCache: Language[];
  static isGettingLanguages = false;
  type: string;
  isActive: boolean;
  allowHtml: boolean;
  allowImage: boolean;
  allowVideo: boolean;
  allowZip: boolean;
  allowAudio: boolean;
  availableForAdServer: boolean;
  availableForDeals: boolean;
  availableForOpenExchange: boolean;
  availableForStoreFront: boolean;
  blockUniqueAdvertiserIDs: number[];
  description: string;
  externalID: string;
  floorCPM: number;
  iabBlockCategories: IABCategory[];
  impressionVerifierVendor: string;
  impressionVerifierVendorExternalId: string;
  lastHeardFromSecondsAgo?: number;
  lastPlayedOnSecondsAgo?: number;
  latitude: number;
  locationID: number;
  location: Location;
  longitude: number;
  loopLength: number;
  mediaType: MediaType;
  mediaTypeID: number;
  minSecondsBetweenAdDomainPlays: number;
  minSecondsBetweenIABCategoryPlays: number;
  name: string;
  networkID: number;
  network: Network;
  operatingHours: string;
  ownerID: number;
  screenHeight: number;
  screenWidth: number;
  siteID: number;
  site: Site;
  spotLength: number;
  minSpotLength: number;
  maxSpotLength: number;
  timezone: string;
  uuid: string;
  openrtbUUID: string;
  unitLanguages: UnitLanguage[];
  hasAudienceData: boolean;
  enableStrictIABCategoriesBlocking: boolean;
  enableStrictIABFrequencyCapping: boolean;
  imageUri: string;
  physicalScreenWidth: number;
  physicalScreenHeight: number;
  facingDirection: string;
  geofence: IUnitGeofenceAPI;
  fallbackCreatives: UnitFallbackCreative[];
  tags: string[];
  isInlineEdited = false;
  lastHeardFromAt?: DateTime;
  createdOnUtc: DateTime;
  modifiedOnUtc: DateTime;
  isLocationIDLocked: boolean;
  isStreetViewImageAvailable: boolean;
  lastHeardFromUtc?: DateTime;
  lastPlayedOnUtc?: DateTime;
  appDomainID: string;
  physicalUnitCount: number;
  hasPhysicalUnitCount: boolean;
  properties = [];
  zipCode: string;
  budgetBreakdownName: string;
  timestamp: DateTime;
  serialNumber?: string;
  unitPlayerIntegration?: UnitPlayerIntegration;

  constructor(
    id = -1,
    isActive = true,
    allowHtml = true,
    allowImage = true,
    allowVideo = true,
    allowZip = true,
    allowAudio = true,
    availableForAdServer = false,
    availableForDeals = false,
    availableForOpenExchange = false,
    availableForStoreFront = false,
    blockUniqueAdvertiserIDs: number[] = [],
    description?: string,
    externalID?: string,
    floorCPM = getStore().getters.membershipSettings?.uiDefaultFloorCpm ?? 10,
    iabBlockCategories: IABCategory[] = [],
    impressionVerifierVendor?: string,
    impressionVerifierVendorExternalId?: string,
    lastHeardFromSecondsAgo?: number,
    lastPlayedOnSecondsAgo?: number,
    latitude?: number,
    locationID?: number,
    location?: Location,
    longitude?: number,
    loopLength = 60,
    mediaTypeID?: number,
    mediaType?: MediaType,
    minSecondsBetweenAdDomainPlays = 0,
    minSecondsBetweenIABCategoryPlays = 0,
    name = '',
    networkID?: number,
    network?: Network,
    operatingHours = FullDaypart,
    ownerID = orgAppStore?.state?.organization?.id,
    screenHeight = 1080,
    screenWidth = 1920,
    siteID?: number,
    site?: Site,
    spotLength = getStore().getters.membershipSettings?.uiDefaultAdDuration ?? 10,
    minSpotLength?: number,
    maxSpotLength?: number,
    timezone?: string,
    uuid?: string,
    openrtbUUID?: string,
    unitLanguages?: UnitLanguage[],
    hasAudienceData?: boolean,
    enableStrictIABCategoriesBlocking = true,
    enableStrictIABFrequencyCapping = false,
    imageUri?: string,
    physicalScreenWidth = null,
    physicalScreenHeight = null,
    facingDirection?: string,
    createdOnUtc?: DateTime,
    modifiedOnUtc?: DateTime,
    geofence?: IUnitGeofenceAPI,
    fallbackCreatives?: UnitFallbackCreative[],
    isLocationIDLocked = false,
    lastHeardFromUtc = null,
    lastPlayedOnUtc = null,
    appDomainID = null,
    tags?: string[],
    physicalUnitCount?: number,
    properties = [],
    zipCode?: string,
    budgetBreakdownName?: string,
    serialNumber?: string,
    unitPlayerIntegration?: UnitPlayerIntegration,
  ) {
    super();
    this.type = type;
    this.id = id;
    this.isActive = isActive;
    this.allowHtml = allowHtml;
    this.allowImage = allowImage;
    this.allowVideo = allowVideo;
    this.allowZip = allowZip;
    this.allowAudio = allowAudio;
    this.availableForAdServer = availableForAdServer;
    this.availableForDeals = availableForDeals;
    this.availableForOpenExchange = availableForOpenExchange;
    this.availableForStoreFront = availableForStoreFront;
    this.blockUniqueAdvertiserIDs = blockUniqueAdvertiserIDs;
    this.description = description;
    this.externalID = externalID;
    this.floorCPM = floorCPM;
    this.iabBlockCategories = iabBlockCategories;
    this.impressionVerifierVendor = impressionVerifierVendor;
    this.impressionVerifierVendorExternalId = impressionVerifierVendorExternalId;
    this.lastHeardFromSecondsAgo = lastHeardFromSecondsAgo;
    this.lastPlayedOnSecondsAgo = lastPlayedOnSecondsAgo;
    this.latitude = latitude;
    this.locationID = locationID;
    this.location = location;
    this.longitude = longitude;
    this.loopLength = loopLength;
    this.mediaType = mediaType;
    this.mediaTypeID = mediaTypeID;
    this.minSecondsBetweenAdDomainPlays = minSecondsBetweenAdDomainPlays;
    this.minSecondsBetweenIABCategoryPlays = minSecondsBetweenIABCategoryPlays;
    this.name = name;
    this.networkID = networkID;
    this.network = network;
    this.operatingHours = operatingHours;
    this.ownerID = ownerID;
    this.screenHeight = screenHeight;
    this.screenWidth = screenWidth;
    this.siteID = siteID;
    this.site = site;
    this.spotLength = spotLength;
    this.minSpotLength = minSpotLength;
    this.maxSpotLength = maxSpotLength;
    this.timezone = timezone;
    this.uuid = uuid;
    this.openrtbUUID = openrtbUUID;
    this.unitLanguages = unitLanguages ?? [];
    this.hasAudienceData = hasAudienceData;
    this.enableStrictIABCategoriesBlocking = enableStrictIABCategoriesBlocking;
    this.enableStrictIABFrequencyCapping = enableStrictIABFrequencyCapping;
    this.imageUri = imageUri;
    this.physicalScreenWidth = physicalScreenWidth;
    this.physicalScreenHeight = physicalScreenHeight;
    this.facingDirection = facingDirection;
    this.geofence = geofence;
    this.fallbackCreatives = fallbackCreatives;
    this.tags = tags;

    this.isInlineEdited = false;
    this.apiObject = null;

    this.lastHeardFromAt =
      this.lastHeardFromSecondsAgo != null ? DateTime.local().minus({ seconds: this.lastHeardFromSecondsAgo }) : null;

    this.createdOnUtc = createdOnUtc;
    this.modifiedOnUtc = modifiedOnUtc;
    this.isLocationIDLocked = isLocationIDLocked;
    this.isStreetViewImageAvailable = null;
    this.lastHeardFromUtc = lastHeardFromUtc;
    this.lastPlayedOnUtc = lastPlayedOnUtc;
    this.appDomainID = appDomainID;
    this.physicalUnitCount = physicalUnitCount;
    this.hasPhysicalUnitCount = physicalUnitCount > 1;
    this.properties = properties;
    this.budgetBreakdownName = budgetBreakdownName;
    this.zipCode = zipCode;
    this.timestamp = DateTime.local();
    this.serialNumber = serialNumber;
    this.unitPlayerIntegration = unitPlayerIntegration;

    //
    // The following property are only used during bulk creation.
    //
    // this.siteExternalID = null;
  }

  static {
    // When the user changes the language, reset the entityProperties cache.
    GlobalEventBus.$on(GlobalEvents.languageChanged, () => {
      Unit.entityPropertiesCached = null;
    });

    // When the user logs out or changes the organization, reset the entityProperties cache.
    GlobalEventBus.$on(GlobalEvents.logout, () => {
      Unit.entityPropertiesCached = null;
    });
    GlobalEventBus.$on(GlobalEvents.organizationChanged, () => {
      Unit.entityPropertiesCached = null;
    });
  }

  get hasPhysicalScreenSize() {
    return (
      this.physicalScreenWidth &&
      this.physicalScreenWidth > 0 &&
      this.physicalScreenHeight &&
      this.physicalScreenHeight > 0
    );
  }

  get physicalScreenSizeFormatted(): { width: string; height: string } {
    if (this.hasPhysicalScreenSize) {
      return {
        width: `${this.physicalScreenWidth}`,
        height: `${this.physicalScreenHeight}`,
      };
    }
    return {
      width: DASH,
      height: DASH,
    };
  }

  get selectiveListFormatted(): string {
    const list = [];
    if (this.availableForAdServer) {
      list.push(capitalize(i18n.global.t('options.selectiveListing.adServer')));
    }
    if (this.availableForDeals) {
      list.push(capitalize(i18n.global.t('options.selectiveListing.deals')));
    }
    if (this.availableForOpenExchange) {
      list.push(capitalize(i18n.global.t('options.selectiveListing.openExchange')));
    }
    if (getStore().state?.orgApp?.organization?.exchange?.isWhiteLabeled && this.availableForStoreFront) {
      list.push(capitalize(i18n.global.t('options.selectiveListing.storeFront')));
    }

    if (list.length === 0) {
      return DASH;
    }

    return list.join(', ');
  }

  get facingDirectionFormatted(): string {
    return this.facingDirection != null ? i18n.global.t(`options.unit.facingDirection.${this.facingDirection}`) : DASH;
  }

  get originalObject(): Unit {
    if (this.apiObject == null) return null;
    if (this.cachedOriginalObject == null) {
      this.cachedOriginalObject = Unit.fromApi(this.apiObject);
    }
    return this.cachedOriginalObject;
  }

  get endpoint(): string {
    return `/units/${this.id}`;
  }

  get displayName(): string {
    return getStore().state?.orgApp?.organizationSettings?.uiEnableDisplayOfSiteIDNextToSiteName
      ? `[${this.externalID}] - ${this.name}`
      : this.name;
  }

  get isActiveDescription(): string {
    if (this.isActive) {
      return capitalize(i18n.global.t('actions.yes'));
    }
    return capitalize(i18n.global.t('actions.no'));
  }

  get availableForAdServerDescription(): string {
    if (this.availableForAdServer) {
      return capitalize(i18n.global.t('actions.yes'));
    }
    return capitalize(i18n.global.t('actions.no'));
  }

  get availableForDealsDescription(): string {
    if (this.availableForDeals) {
      return capitalize(i18n.global.t('actions.yes'));
    }
    return capitalize(i18n.global.t('actions.no'));
  }

  get availableForOpenExchangeDescription(): string {
    if (this.availableForOpenExchange) {
      return capitalize(i18n.global.t('actions.yes'));
    }
    return capitalize(i18n.global.t('actions.no'));
  }

  get availableForStoreFrontDescription(): string {
    if (this.availableForStoreFront) {
      return capitalize(i18n.global.t('actions.yes'));
    }
    return capitalize(i18n.global.t('actions.no'));
  }

  get allowHtmlDescription(): string {
    if (this.allowHtml) {
      return capitalize(i18n.global.t('actions.yes'));
    }
    return capitalize(i18n.global.t('actions.no'));
  }

  get allowImageDescription(): string {
    if (this.allowImage) {
      return capitalize(i18n.global.t('actions.yes'));
    }
    return capitalize(i18n.global.t('actions.no'));
  }

  get allowVideoDescription(): string {
    if (this.allowVideo) {
      return capitalize(i18n.global.t('actions.yes'));
    }
    return capitalize(i18n.global.t('actions.no'));
  }

  get allowZipDescription(): string {
    if (this.allowZip) {
      return capitalize(i18n.global.t('actions.yes'));
    }
    return capitalize(i18n.global.t('actions.no'));
  }

  get allowAudioDescription(): string {
    if (this.allowAudio) {
      return capitalize(i18n.global.t('actions.yes'));
    }
    return capitalize(i18n.global.t('actions.no'));
  }

  get blockUniqueAdvertisersDescription(): string {
    const advertisers = this.blockUniqueAdvertiserIDs.map((x) =>
      getStore().state?.orgApp.Advertisers.availableUniqueAdvertisers.find((y) => y.id === x),
    );
    if (advertisers.length === 0 || advertisers.filter((x) => x === null).length > 0) {
      return DASH;
    }
    return advertisers.map((x) => x?.name || '').join(', ');
  }

  get iabBlockCategoriesDescription(): string {
    const list = [];
    this.iabBlockCategories.forEach((cat) => list.push(cat.name));

    if (list.length === 0) {
      return DASH;
    }

    return list.join(', ');
  }

  get enableStrictIABCategoriesBlockingDescription(): string {
    if (this.enableStrictIABCategoriesBlocking) {
      return capitalize(i18n.global.t('actions.yes'));
    }
    return capitalize(i18n.global.t('actions.no'));
  }

  get enableStrictIABFrequencyCappingDescription(): string {
    if (this.enableStrictIABFrequencyCapping) {
      return capitalize(i18n.global.t('actions.yes'));
    }
    return capitalize(i18n.global.t('actions.no'));
  }

  get loopLengthFormatted(): string {
    if (this.loopLength !== null && this.loopLength !== undefined) {
      return `${this.loopLength} ${i18n.global.t('names.second', { n: 2 })}`;
    }
    return DASH;
  }

  get tagsFormatted(): string {
    if (this.tags.length > 0) {
      return this.tags.map((x) => x.charAt(0).toUpperCase() + x.substring(1)).join(', ');
    }
    return DASH;
  }

  get spotLengthFormatted(): string {
    if (this.minSpotLength != null) {
      if (this.minSpotLength !== this.maxSpotLength) {
        return (
          `${this.spotLength} ${i18n.global.t('names.secondAbbr', { n: this.spotLength })} ` +
          `(${i18n.global.t('names.min', { n: 1 })}: ${this.minSpotLength} ${i18n.global.t('names.secondAbbr', { n: this.minSpotLength })}, ` +
          `${i18n.global.t('names.max', { n: 1 })}: ${this.maxSpotLength} ${i18n.global.t('names.secondAbbr', { n: this.maxSpotLength })})`
        );
      }
      return `${this.minSpotLength} ${i18n.global.t('names.secondAbbr', { n: this.minSpotLength })}`;
    }
    if (this.spotLength != null) {
      return `${this.spotLength} ${i18n.global.t('names.secondAbbr', { n: 2 })}`;
    }
    return DASH;
  }

  get floorCPMFormatted(): string {
    if (this.floorCPM !== null && this.floorCPM !== undefined) {
      return i18n.global.n(this.floorCPM, 'currency');
    }
    return DASH;
  }

  get minSecondsBetweenAdDomainPlaysFormatted(): string {
    if (this.minSecondsBetweenAdDomainPlays !== null && this.minSecondsBetweenAdDomainPlays !== undefined) {
      return `${this.minSecondsBetweenAdDomainPlays} ${i18n.global.t('names.second', { n: 2 })}`;
    }
    return DASH;
  }

  get minSecondsBetweenIABCategoryPlaysFormatted(): string {
    if (this.minSecondsBetweenIABCategoryPlays !== null && this.minSecondsBetweenIABCategoryPlays !== undefined) {
      return `${this.minSecondsBetweenIABCategoryPlays} ${i18n.global.t('names.second', { n: 2 })}`;
    }
    return DASH;
  }

  get lat(): number {
    return this.latitude;
  }

  get lng(): number {
    return this.longitude;
  }

  get mediaTypeName(): string {
    if (this.mediaType != null) {
      return this.mediaType.formattedFullName;
    }
    return DASH;
  }

  get siteName(): string {
    if (this.site != null) {
      return this.site.name;
    }
    return DASH;
  }

  get networkName(): string {
    if (this.network !== null) {
      return this.network.name;
    }
    const networks = peekQueryInScope(useNetworks);
    const match = networks.find((x) => x.id === this.networkID);
    if (match !== undefined) {
      return match.name;
    }
    return DASH;
  }

  get ownerName(): string {
    const mediaOwners = peekQueryInScope(useMediaOwners);
    const match = mediaOwners.find((x) => x.id === this.ownerID);
    if (match) return match.name;
    return DASH;
  }

  get locationName(): string {
    const match = getStore().state?.orgApp.ReferenceData.locations.find((x) => x.id === this.locationID);
    if (match !== undefined) {
      return match.name;
    }
    return DASH;
  }

  get languagesDescription(): string {
    const languages = this.unitLanguages.map((x) => x.language);
    if (languages.length === 0 || languages.filter((x) => x === null).length > 0) {
      return DASH;
    }
    return languages.map((x) => x.name).join(', ');
  }

  get lastPlayedTimeFormatted(): string {
    return this.lastHeardFromUtc != null ? this.lastHeardFromUtc.toLocal().toLocaleString(DateTime.DATETIME_MED) : DASH;
  }

  get didRequestAnAdInTheLastHour(): boolean {
    return this.lastHeardFromSecondsAgo !== null && this.lastHeardFromSecondsAgo < 3600;
  }

  get didRequestAnAdInTheLastHourDescription(): string {
    if (this.didRequestAnAdInTheLastHour) {
      return capitalize(i18n.global.t('actions.yes'));
    }
    return capitalize(i18n.global.t('actions.no'));
  }

  get hasAudienceDataDescription(): string {
    if (this.hasAudienceData) {
      return capitalize(i18n.global.t('actions.yes'));
    }
    return capitalize(i18n.global.t('actions.no'));
  }

  get streetViewImageUrl(): string {
    return `https://maps.googleapis.com/maps/api/streetview?source=outdoor&size=536x402&location=${this.latitude},${this.longitude}&key=${GoogleMapsApiKey}`;
  }

  get isStreetViewImageAvailableUrl(): string {
    return `https://maps.googleapis.com/maps/api/streetview/metadata?source=outdoor&size=536x402&location=${this.latitude},${this.longitude}&key=${GoogleMapsApiKey}`;
  }

  get operatingHoursDescription(): string {
    return getDaypartDescription(this.operatingHours);
  }
  get operatingHoursDescriptionString(): string {
    return getDaypartDescription(this.operatingHours, false);
  }

  get timeEllapsedSinceLastHeardFrom(): string {
    if (this.lastHeardFromAt === null) {
      return DASH;
    }
    return DateTime.local().minus({ seconds: this.lastHeardFromSecondsAgo }).toRelative();
  }

  checkStreetViewImageAvailability() {
    if (this.isStreetViewImageAvailable != null) {
      return;
    }
    axios.get(this.isStreetViewImageAvailableUrl).then((streetViewResult) => {
      rootStore.dispatch('updateUnitIsStreetViewImageAvailable', {
        unit: this,
        value: streetViewResult.data.status === 'OK',
      });
    });
  }

  get createdOnFormatted(): string {
    return this.createdOnUtc != null ? this.createdOnUtc.toLocal().toLocaleString(DateTime.DATETIME_MED) : DASH;
  }

  get modifiedOnFormatted(): string {
    return this.modifiedOnUtc != null ? this.modifiedOnUtc.toLocal().toLocaleString(DateTime.DATETIME_MED) : DASH;
  }

  get createdOn(): DateTime {
    return this.createdOnUtc != null ? this.createdOnUtc.toLocal() : null;
  }

  get modifiedOn(): DateTime {
    return this.modifiedOnUtc != null ? this.modifiedOnUtc.toLocal() : null;
  }

  get lastHeardFrom(): DateTime {
    return this.lastHeardFromUtc != null ? this.lastHeardFromUtc.toLocal() : null;
  }

  get lastHeardFromFormatted(): string {
    return this.lastHeardFromUtc != null ? this.lastHeardFromUtc.toLocal().toLocaleString(DateTime.DATETIME_MED) : DASH;
  }

  get lastPlayedOn(): DateTime {
    return this.lastPlayedOnUtc != null ? this.lastPlayedOnUtc.toLocal() : null;
  }

  get lastPlayedOnFormatted(): string {
    return this.lastPlayedOnUtc != null ? this.lastPlayedOnUtc.toLocal().toLocaleString(DateTime.DATETIME_MED) : DASH;
  }

  get idAndName(): string {
    return `${this.externalID} - ${this.name}`;
  }

  get geofenceBoundingBox(): [[number, number], [number, number]] {
    if (this.geofence == null) {
      return null;
    }
    let { coordinates } = this.geofence.geo_json;
    const findCoordinates = (allCoordinates) => {
      if (allCoordinates.length === 1) {
        findCoordinates(allCoordinates[0]);
      } else {
        coordinates = allCoordinates;
      }
    };

    findCoordinates(this.geofence.geo_json.coordinates);

    const lats = coordinates.map((x) => x[1]);
    const lngs = coordinates.map((x) => x[0]);
    return [
      [Math.min(...lngs), Math.min(...lats)],
      [Math.max(...lngs), Math.max(...lats)],
    ];
  }

  get unitOrSiteLat(): string | number {
    return this.latitude || this.site.latitude;
  }

  get unitOrSiteLng(): string | number {
    return this.longitude || this.site.longitude;
  }

  get operatingHoursRanges(): string {
    return daypartToRanges(this.operatingHours);
  }

  get physicalUnitCountFormatted(): string {
    return this.physicalUnitCount ? `${this.physicalUnitCount}` : DASH;
  }

  get unitID(): number {
    return this.id;
  }

  get siteExternalID(): string {
    return this.site?.externalID;
  }

  get locationHierarchy() {
    return this.location.hierarchy;
  }

  get customFields() {
    const customFields = rootStore.getters['orgApp/organizationUnitCustomFields'];
    return this.properties.filter((x) => customFields.some((cf) => cf.key === x.key));
  }

  get isNew(): boolean {
    return this.id === -1;
  }

  getExtraValidation(): Record<string, IExtraValidation> {
    const extras: Record<string, IExtraValidation> = {};
    if (this.operatingHours != null && !isValidDaypart(this.operatingHours)) {
      extras.operatingHours = {
        message: i18n.global.t('messages.operatingHoursAreNotValid'),
        preEvaluated: true,
      };
    }
    if (this.unitLanguages.length === 0) {
      extras.unitLanguages = {
        message: i18n.global.t('messages.theLanguagesFieldIsMandatory'),
        preEvaluated: true,
      };
    }
    if (
      (this.minSpotLength != null || this.maxSpotLength != null) &&
      (this.minSpotLength == null || this.maxSpotLength == null)
    ) {
      extras.minSpotLength = {
        message: i18n.global.t('messages.bothMinAndMaxAdDurationsMustBeSpecified'),
        preEvaluated: true,
      };
    } else if (this.minSpotLength != null && this.maxSpotLength != null && this.minSpotLength > this.maxSpotLength) {
      extras.minSpotLength = {
        message: i18n.global.t('messages.minAdDurationMustBeLessThanMaxAdDuration'),
        preEvaluated: true,
      };
    }
    if (getStore().state?.orgApp?.organizationSettings?.isUnitVenueTypeRequired && this.mediaTypeID === null) {
      extras.mediaTypeID = {
        message: i18n.global.t('messages.mediaTypeCannotBeEmpty'),
        preEvaluated: true,
      };
    }
    return extras;
  }

  async getChanges(): Promise<IEntityChange<Unit>[]> {
    if (this.isNew) return [];

    return super.getValidatableChanges<Unit>(this.originalObject, Unit.entityProperties, ['siteExternalID']);
  }

  isPristine(): boolean {
    if (this.isNew) return true;

    const ignoreProperties = [
      'allow_flash',
      'archived',
      'created_on_utc',
      'geofence_id',
      'geohash',
      'has_audience_data',
      'hex9',
      'iab_blacklist_level1_values',
      'last_heard_from_seconds_ago',
      'last_heard_from_utc',
      'last_played_on_seconds_ago',
      'last_played_on_utc',
      'location',
      'location_id',
      'location_id_overridden',
      'media_type',
      'mediatype_name',
      'min_seconds_between_creative_category_plays',
      'modified_on_utc',
      'openrtb_uuid',
      'orientation',
      'point',
      'site',
      'unit_languages',
      'unitfallbackcreatives',
      'weekly_impressions',
      'weekly_traffic',
      'yield_manager_enabled',
      'zip_code_overridden',
      'unit_player_integration',
    ];
    const diffs = getDiff(
      this.apiObject,
      this.toApi(),
      ignoreProperties,
      [],
      [
        { key: 'physical_screen_height_cm', values: [0, null] },
        { key: 'physical_screen_width_cm', values: [0, null] },
      ],
      [{ key: 'serial_number', values: [''] }], // Exclude 'REMOVE' diff of 'serial_number' when the value is ''
    );
    const locationDiffs = getDiff(this.apiObject.location, this.toApi().location, [
      'external_id',
      'latitude',
      'longitude',
      'shapes',
      'subregion_id',
      'version',
    ]);
    const siteDiffs = getDiff(this.apiObject.site, this.toApi().site, [
      'archived',
      'created_on_utc',
      'geohash',
      'location_id_overridden',
      'modified_on_utc',
      'point',
    ]);
    const unitLanguageDiffs = getArrayDiff(this.apiObject.unit_languages, this.toApi().unit_languages, [
      'created_on_utc',
      'modified_on_utc',
    ]);

    if (diffs.length === 0 && locationDiffs.length === 0 && siteDiffs.length === 0 && unitLanguageDiffs.length === 0)
      return true;
    DatadogApi.logUnexpectedChange([...diffs, ...locationDiffs, ...siteDiffs, ...unitLanguageDiffs]);
    return false;
  }

  static fromApi(apiObject: IUnitAPI): Unit {
    const iabCategoryOptions = IABCategories.options();

    const unit = new Unit(
      apiObject.unit_id,
      apiObject.active,
      apiObject.allow_html,
      apiObject.allow_image,
      apiObject.allow_video,
      apiObject.allow_zip,
      apiObject.allow_audio,
      apiObject.available_for_adserver,
      apiObject.available_for_deals,
      apiObject.available_for_open_exchange,
      apiObject.available_for_storefront,
      apiObject.blacklist_unique_advertiser_ids,
      apiObject.description,
      apiObject.external_id,
      apiObject.floor_cpm,
      apiObject.iab_blacklist_categories != null
        ? apiObject.iab_blacklist_categories.map((x) => iabCategoryOptions.find((o) => o.id === x)).filter((x) => x)
        : null,
      apiObject.impression_verifier_vendor,
      apiObject.impression_verifier_vendor_external_id,
      apiObject.last_heard_from_seconds_ago,
      apiObject.last_played_on_seconds_ago,
      apiObject.latitude,
      apiObject.location ? apiObject.location.location_id : null,
      apiObject.location ? Location.fromApi(apiObject.location) : null,
      apiObject.longitude,
      apiObject.loop_length,
      apiObject.mediatype_id,
      apiObject.media_type ? MediaType.fromApi(apiObject.media_type) : null,
      apiObject.min_seconds_between_ad_domain_plays,
      apiObject.min_seconds_between_iab_category_plays,
      apiObject.name,
      apiObject.network_id,
      apiObject.network != null ? Network.fromApi(apiObject.network) : null,
      apiObject.operating_hours || FullDaypart,
      apiObject.owner_id,
      apiObject.screen_height,
      apiObject.screen_width,
      apiObject.site_id,
      apiObject.site != null ? Site.fromApi(apiObject.site) : null,
      apiObject.spot_length,
      apiObject.min_spot_length,
      apiObject.max_spot_length,
      apiObject.timezone,
      apiObject.uuid,
      apiObject.openrtb_uuid,
      apiObject.unit_languages !== undefined ? apiObject.unit_languages.map((x) => UnitLanguage.fromApi(x)) : [],
      apiObject.has_audience_data,
      apiObject.enable_strict_iab_blacklisting,
      apiObject.enable_strict_iab_frequency_capping,
      apiObject.image_uri,
      apiObject.physical_screen_width_cm,
      apiObject.physical_screen_height_cm,
      apiObject.facing_direction,
      apiObject.created_on_utc != null ? DateTime.fromISO(apiObject.created_on_utc, { zone: 'utc' }) : null,
      apiObject.modified_on_utc != null ? DateTime.fromISO(apiObject.modified_on_utc, { zone: 'utc' }) : null,
      apiObject.geofence,
      apiObject.unitfallbackcreatives != null
        ? apiObject.unitfallbackcreatives.map((x) => UnitFallbackCreative.fromApi(x))
        : [],
      apiObject.location_id_overridden || true,
      apiObject.last_heard_from_utc != null ? DateTime.fromISO(apiObject.last_heard_from_utc, { zone: 'utc' }) : null,
      apiObject.last_played_on_utc != null ? DateTime.fromISO(apiObject.last_played_on_utc, { zone: 'utc' }) : null,
      apiObject.app_domain_id,
      apiObject.tags,
      apiObject.physical_unit_count,
      apiObject.unit_properties !== undefined ? apiObject.unit_properties.map((x) => UnitProperty.fromApi(x)) : [],
      apiObject.zip_code,
      null,
      apiObject.serial_number,
      apiObject.unit_player_integration != null
        ? UnitPlayerIntegration.fromApi(apiObject.unit_player_integration)
        : null,
    );
    unit.apiObject = apiObject;
    return unit;
  }

  toApi(): IUnitAPI {
    const apiObject = {
      active: this.isActive,
      allow_html: this.allowHtml,
      allow_image: this.allowImage,
      allow_video: this.allowVideo,
      allow_zip: this.allowZip,
      allow_audio: this.allowAudio,
      available_for_adserver: this.availableForAdServer,
      available_for_deals: this.availableForDeals,
      available_for_open_exchange: this.availableForOpenExchange,
      available_for_storefront: this.availableForStoreFront,
      app_domain_id: this.appDomainID,
      blacklist_unique_advertiser_ids: this.blockUniqueAdvertiserIDs,
      description: this.description,
      enable_strict_iab_blacklisting: this.enableStrictIABCategoriesBlocking,
      enable_strict_iab_frequency_capping: this.enableStrictIABFrequencyCapping,
      external_id: this.externalID,
      facing_direction: this.facingDirection,
      floor_cpm: this.floorCPM,
      geofence: this.geofence,
      iab_blacklist_categories: this.iabBlockCategories != null ? this.iabBlockCategories.map((x) => x.id) : undefined,
      image_uri: this.imageUri,
      impression_verifier_vendor: this.impressionVerifierVendor,
      impression_verifier_vendor_external_id: this.impressionVerifierVendorExternalId,
      latitude: this.latitude,
      location_id_overridden: this.isLocationIDLocked,
      location: this.location?.toApi(),
      longitude: this.longitude,
      loop_length: this.loopLength,
      max_spot_length: this.maxSpotLength,
      mediatype_id: this.mediaTypeID,
      min_seconds_between_ad_domain_plays: this.minSecondsBetweenAdDomainPlays,
      min_seconds_between_iab_category_plays: this.minSecondsBetweenIABCategoryPlays,
      min_spot_length: this.minSpotLength,
      name: this.name,
      network_id: this.networkID,
      operating_hours: this.operatingHours,
      owner_id: this.ownerID,
      physical_screen_height_cm: this.physicalScreenHeight,
      physical_screen_width_cm: this.physicalScreenWidth,
      screen_height: this.screenHeight,
      screen_width: this.screenWidth,
      site: this.site?.toApi(),
      site_id: this.siteID,
      spot_length: this.spotLength,
      timezone: this.timezone,
      unit_id: this.id,
      unit_languages: this.unitLanguages.map((x) => x.toApi()),
      uuid: this.uuid,
      tags: this.tags,
      physical_unit_count: this.physicalUnitCount,
      unit_properties: this.properties,
      zip_code: this.zipCode,
      serial_number: this.serialNumber,
    };

    if (this.isInlineEdited) {
      Unit.expandedProperties.forEach((property) => delete apiObject[property]);
    }

    return Unit.convertModelToEntityPropertyTypes(apiObject);
  }

  static fromObject(obj: Unit): Unit {
    return super.fromObject(obj, new Unit());
  }

  static validatorCreateRules(): Record<string, string | IComplexValidationRule> {
    return {
      allowHtml: 'required',
      allowImage: 'required',
      allowVideo: 'required',
      allowAudio: 'required',
      allowZip: 'required',
      availableForAdServer: 'required',
      availableForDeals: 'required',
      availableForOpenExchange: 'required',
      availableForStoreFront: 'required',
      description: 'max:1000',
      externalID: 'required|max:75|not_empty_or_whitespace',
      floorCPM: 'required|numeric:float|min_value:0|max_value:999999.99',
      isActive: 'required',
      latitude: 'required|numeric:float|min_value:-90|max_value:90',
      longitude: 'required|numeric:float|min_value:-180|max_value:180',
      loopLength: 'numeric:int',
      minSpotLength: 'numeric:int|min_value:1',
      maxSpotLength: 'numeric:int',
      mediaTypeID: 'numeric:int',
      minSecondsBetweenAdDomainPlays: 'required|min_value:0',
      minSecondsBetweenIABCategoryPlays: 'required|min_value:0',
      name: 'required|max:191|not_empty_or_whitespace',
      networkID: 'required',
      operatingHours: 'required|min:168|max:168',
      physicalScreenHeight: 'numeric:int|min_value:0',
      physicalScreenWidth: 'numeric:int|min_value:0',
      screenHeight: 'required|numeric:int|min_value:0',
      screenWidth: 'required|numeric:int|min_value:0',
      siteID: 'required',
      spotLength: 'required|numeric:int|min_value:1|max_value:2000000000',
      //timezone: 'required',
      unitLanguages: 'required',
      //uuid: { rule: 'uuid', formName: 'unitUUID' },
      physicalUnitCount: 'numeric:int|min_value:2',
    };
  }

  static get expands(): string[] {
    return [
      'site',
      'unit_languages',
      'media_type',
      'unit_iab_blacklist',
      'unit_advertiser_blacklist',
      'geofence',
      'unitfallbackcreatives',
      'location',
      'unit_properties',
      'unit_player_integration',
    ];
    //
    // UPDATE EXPANDEDPROPERTIES IF YOU ADD NEW EXPAND QUERY PARAMETER
    //
  }

  static get expandedProperties(): string[] {
    return ['geofence', 'unitfallbackcreative_id', 'location_id', 'location_id_overridden'];
  }

  static get bulkEditProperties(): EntityProperty<Unit>[] {
    return [
      this.entityProperties.facingDirection,
      this.entityProperties.site,
      this.entityProperties.networkName,
      this.entityProperties.mediaTypeName,
      this.entityProperties.floorCPM,
      this.entityProperties.languages,
      this.entityProperties.tags,
      this.entityProperties.screenWidth,
      this.entityProperties.screenHeight,
      this.entityProperties.availableForAdServer,
      this.entityProperties.availableForDeals,
      this.entityProperties.availableForOpenExchange,
      this.entityProperties.isActive,
      this.entityProperties.spotLength,
      this.entityProperties.minSpotLength,
      this.entityProperties.maxSpotLength,
      this.entityProperties.allowHtml,
      this.entityProperties.allowImage,
      this.entityProperties.allowVideo,
      this.entityProperties.allowZip,
      this.entityProperties.allowAudio,
      this.entityProperties.minSecondsBetweenAdDomainPlays,
      this.entityProperties.iabBlockCategories,
      this.entityProperties.impressionVerifierVendor,
      this.entityProperties.impressionVerifierVendorExternalId,
      this.entityProperties.minSecondsBetweenIABCategoryPlays,
      this.entityProperties.enableStrictIABFrequencyCapping,
      this.entityProperties.operatingHoursDescriptionString,
      this.entityProperties.blockUniqueAdvertiserIDs,
      this.entityProperties.fallbackCreatives,
      this.entityProperties.physicalUnitCount,
    ];
  }

  static get bulkV4Properties() {
    return [
      this.entityProperties.externalID,
      this.entityProperties.name,
      this.entityProperties.facingDirection,
      this.entityProperties.siteExternalID,
      this.entityProperties.networkName,
      this.entityProperties.mediaTypeName,
      this.entityProperties.floorCPM,
      this.entityProperties.languages,
      this.entityProperties.tags,
      this.entityProperties.screenWidth,
      this.entityProperties.screenHeight,
      this.entityProperties.isActive,
      this.entityProperties.availableForAdServer,
      this.entityProperties.availableForDeals,
      this.entityProperties.availableForOpenExchange,
      this.entityProperties.spotLength,
      this.entityProperties.minSpotLength,
      this.entityProperties.maxSpotLength,
      this.entityProperties.allowHtml,
      this.entityProperties.allowImage,
      this.entityProperties.allowVideo,
      this.entityProperties.allowZip,
      this.entityProperties.allowAudio,
      this.entityProperties.minSecondsBetweenAdDomainPlays,
      this.entityProperties.iabBlockCategories,
      this.entityProperties.impressionVerifierVendor,
      this.entityProperties.impressionVerifierVendorExternalId,
      this.entityProperties.enableStrictIABCategoriesBlocking,
      this.entityProperties.enableStrictIABFrequencyCapping,
      this.entityProperties.minSecondsBetweenIABCategoryPlays,
      this.entityProperties.latitude,
      this.entityProperties.longitude,
      this.entityProperties.timezone,
      this.entityProperties.operatingHoursDescriptionString,
    ];
  }

  static get entityProperties(): Record<string, EntityProperty<Unit>> {
    if (Unit.entityPropertiesCached != null) {
      return Unit.entityPropertiesCached;
    }
    const action = (unit) => rootStore.dispatch('orgApp/saveUnit', { unit, showLoader: false });
    const getSitesAction = ({ queryParams, showLoader }: { queryParams?: QueryParams; showLoader: boolean }) =>
      rootStore.dispatch('orgApp/getSites', { queryParams, showLoader });

    const getExtraValidation = (unit) => unit.getExtraValidation();

    Unit.entityPropertiesCached = {
      id: new EntityProperty({
        label: capitalize(i18n.global.t('names.id', { n: 1 })),
        property: 'unit_id',
        type: TableColumnTypes.string,
        editablePropertyName: 'id',
      }),
      externalID: new EntityProperty({
        label: capitalize(i18n.global.t('names.screenID', { n: 1 })),
        property: 'external_id',
        type: TableColumnTypes.string,
        editablePropertyName: 'externalID',
        mutation: UPDATE_UNIT_EXTERNALID,
        editableClassName: type,
        bulkCreateOrderIndex: 10,
        action,
        getExtraValidation,
      }),
      name: new EntityProperty({
        label: capitalize(i18n.global.t('names.unitName', { n: 1 })),
        property: 'name',
        type: TableColumnTypes.string,
        editablePropertyName: 'name',
        mutation: UPDATE_UNIT_NAME,
        editableClassName: type,
        bulkCreateOrderIndex: 20,
        action,
        getExtraValidation,
      }),
      unitID: new EntityProperty({
        label: capitalize(i18n.global.t('names.unitID', { n: 1 })),
        property: 'unit_id',
        type: TableColumnTypes.string,
      }),
      uuid: new EntityProperty({
        label: i18n.global.t('messages.unitUUID'),
        property: 'uuid',
        type: TableColumnTypes.uuid,
        editablePropertyName: 'uuid',
        mutation: UPDATE_UNIT_UUID,
        editableClassName: type,
        action,
        getExtraValidation,
      }),
      serialNumber: new EntityProperty({
        label: i18n.global.t('names.serialNumber'),
        property: 'serial_number',
        type: TableColumnTypes.string,
        editablePropertyName: 'serialNumber',
        mutation: ({ value, unit }: { value: string; unit: Unit }) => {
          unit.serialNumber = value;
        },
        editableClassName: type,
        action,
        getExtraValidation,
      }),
      openrtbUUID: new EntityProperty({
        label: i18n.global.t('messages.openrtbUUID'),
        type: TableColumnTypes.uuid,
      }),
      description: new EntityProperty({
        label: capitalize(i18n.global.t('names.internalNote', { n: 2 })),
        property: 'description',
        type: TableColumnTypes.string,
        editablePropertyName: 'description',
        mutation: UPDATE_UNIT_DESCRIPTION,
        editableClassName: type,
        preMutationTransform: (x) => (x === '' ? null : x),
        action,
        getExtraValidation,
      }),
      isActive: new EntityProperty({
        label: capitalize(i18n.global.t('states.enabled')),
        property: 'active',
        type: TableColumnTypes.boolean,
        editablePropertyName: 'isActive',
        mutation: UPDATE_UNIT_ISACTIVE,
        editableClassName: type,
        action,
        getExtraValidation,
        canAffectAdDeliveryWhenModified: true,
      }),
      siteExternalID: new EntityProperty({
        label: capitalize(i18n.global.t('names.siteID', { n: 1 })),
        property: 'site.external_id',
        type: TableColumnTypes.string,
        bulkCreateOrderIndex: 40,
        editableClassName: type,
        isProxyForProperty: {
          property: 'site_id',
          editablePropertyName: 'siteID',
          validationError: i18n.global.t('messages.siteDoesNotExist'),
        },
        mutation: UPDATE_UNIT_SITEID,
        proxyPropertyMapper: async (ids?: string) => {
          const qp = QueryParams.all(FilterCollection.for('external_id', ids, FilterComparisonOperator.IN));
          qp.include = ['site_id', 'external_id'];
          const result = await qp.run(getSitesAction);
          return result.items.map((x) => ({ from: x.externalID, to: x.id }));
        },
        action,
        getExtraValidation,
      }),
      site: new EntityProperty({
        label: capitalize(i18n.global.t('names.site', { n: 1 })),
        property: 'site.name',
        type: TableColumnTypes.string,
        mutation: UPDATE_UNIT_SITEID,
        editableClassName: type,
        editablePropertyName: 'siteID',
        source: (x) => rootStore.dispatch('orgApp/getSites', x),
        sourceFilterProperty: 'site_id',
        sourceLabel: 'displayName',
        sourceID: 'id',
        sourceSupportsAutoComplete: true,
        sourceSearchTermToFiltersTransform: (search) => [
          new FilterGroup(
            [
              new Filter('name', [search], FilterComparisonOperator.STARTS_WITH),
              new Filter('external_id', [search], FilterComparisonOperator.STARTS_WITH),
            ],
            FilterLogicalOperator.OR,
          ),
        ],
        inputElementWidth: '40rem',
        postMutationCallback: (value, entity) => {
          entity.site = value;
        },
        action,
        getExtraValidation,
      }),
      didRequestAnAdInTheLastHour: new EntityProperty({
        label: capitalize(i18n.global.t('messages.adRequestedInTheLastHour')),
        type: TableColumnTypes.boolean,
      }),
      lastHeardFrom: new EntityProperty({
        label: capitalize(i18n.global.t('messages.lastAdRequest')),
        property: 'last_heard_from_utc',
        type: TableColumnTypes.datetime,
      }),
      lastPlayedOn: new EntityProperty({
        label: capitalize(i18n.global.t('messages.lastPlayedOn')),
        property: 'last_played_on_utc',
        type: TableColumnTypes.datetime,
      }),
      floorCPM: new EntityProperty({
        label: capitalize(i18n.global.t('names.floorCPM', { n: 1 })),
        property: 'floor_cpm',
        type: TableColumnTypes.currency,
        editablePropertyName: 'floorCPM',
        mutation: UPDATE_UNIT_FLOORCPM,
        editableClassName: type,
        action,
        getExtraValidation,
        canAffectAdDeliveryWhenModified: true,
        defaultValue: 10,
      }),
      hasAudienceData: new EntityProperty({
        label: capitalize(i18n.global.t('messages.hasImpressionData')),
        property: 'has_audience_data',
        type: TableColumnTypes.boolean,
      }),
      screenWidth: new EntityProperty({
        label: capitalize(i18n.global.t('names.screenWidthPx', { n: 1 })),
        property: 'screen_width',
        type: TableColumnTypes.integer,
        editablePropertyName: 'screenWidth',
        mutation: UPDATE_UNIT_SCREENWIDTH,
        editableClassName: type,
        action,
        getExtraValidation,
        canAffectAdDeliveryWhenModified: true,
      }),
      screenHeight: new EntityProperty({
        label: capitalize(i18n.global.t('names.screenHeightPx', { n: 1 })),
        property: 'screen_height',
        type: TableColumnTypes.integer,
        editablePropertyName: 'screenHeight',
        mutation: UPDATE_UNIT_SCREENHEIGHT,
        editableClassName: type,
        action,
        getExtraValidation,
        canAffectAdDeliveryWhenModified: true,
      }),
      latitude: new EntityProperty({
        label: capitalize(i18n.global.t('names.latitude', { n: 1 })),
        property: 'latitude',
        type: TableColumnTypes.float,
        editablePropertyName: 'latitude',
        mutation: UPDATE_UNIT_LATITUDE,
        editableClassName: type,
        action,
        getExtraValidation,
        canAffectAdDeliveryWhenModified: true,
      }),
      longitude: new EntityProperty({
        label: capitalize(i18n.global.t('names.longitude', { n: 1 })),
        property: 'longitude',
        type: TableColumnTypes.float,
        editablePropertyName: 'longitude',
        mutation: UPDATE_UNIT_LONGITUDE,
        editableClassName: type,
        action,
        getExtraValidation,
        canAffectAdDeliveryWhenModified: true,
      }),
      zipCode: new EntityProperty({
        label: capitalize(i18n.global.t('names.zip_code', { n: 1 })),
        property: 'zip_code',
        type: TableColumnTypes.string,
      }),
      mediaTypeName: new EntityProperty({
        label: capitalize(i18n.global.t('names.venueType', { n: 1 })),
        property: 'mediatype_id',
        type: TableColumnTypes.string,
        source: () => runQueryInScope(useMediaTypesFlatten),
        inlineEditingOptionsSource: () => runQueryInScope(useMediaTypesFlatten),
        sourceFilterProperty: 'mediatype_id',
        editablePropertyName: 'mediaTypeID',
        mutation: UPDATE_UNIT_MEDIATYPEID,
        editableClassName: type,
        postMutationCallback: (value, entity) => {
          entity.mediaType = value;
        },
        action,
        getExtraValidation,
        canAffectAdDeliveryWhenModified: true,
      }),
      networkName: new EntityProperty({
        label: capitalize(i18n.global.t('names.network', { n: 1 })),
        property: 'network_id',
        type: TableColumnTypes.string,
        source: () => runQueryInScope(useNetworks),
        sourceFilterProperty: 'network_id',
        editablePropertyName: 'networkID',
        mutation: UPDATE_UNIT_NETWORKID,
        editableClassName: type,
        postMutationCallback: (value, entity) => {
          entity.network = value;
        },
        action,
        getExtraValidation,
        canAffectAdDeliveryWhenModified: true,
      }),
      locationHierarchy: new EntityProperty({
        label: capitalize(i18n.global.t('names.geographicContext', { n: 1 })),
        property: 'location.hierarchy',
        type: TableColumnTypes.string,
      }),
      tags: new EntityProperty({
        label: capitalize(i18n.global.t('names.tag', { n: 2 })),
        property: 'tags', // related to bug HV-4514
        type: TableColumnTypes.string,
        editablePropertyName: 'tags',
        mutation: UPDATE_UNIT_TAGS,
        editableClassName: type,
        source: (x) => rootStore.dispatch('orgApp/getTags', x),
        sourceID: 'tag',
        sourceLabel: 'tag',
        editablePropertyAcceptsMultipleValues: true,
        action,
        getExtraValidation,
        canAffectAdDeliveryWhenModified: true,
      }),
      availableForAdServer: new EntityProperty({
        label: capitalize(i18n.global.t('messages.availableForAdServer')),
        property: 'available_for_adserver',
        type: TableColumnTypes.boolean,
        editablePropertyName: 'availableForAdServer',
        mutation: UPDATE_UNIT_AD_SERVER_AVAILABLITY,
        editableClassName: type,
        action,
        getExtraValidation,
        canAffectAdDeliveryWhenModified: true,
      }),
      availableForDeals: new EntityProperty({
        label: capitalize(i18n.global.t('messages.availableForDeals')),
        property: 'available_for_deals',
        type: TableColumnTypes.boolean,
        editablePropertyName: 'availableForDeals',
        mutation: UPDATE_UNIT_DEAL_AVAILABILITY,
        editableClassName: type,
        action,
        getExtraValidation,
        canAffectAdDeliveryWhenModified: true,
      }),
      availableForOpenExchange: new EntityProperty({
        label: capitalize(i18n.global.t('messages.availableForOpenExchange')),
        property: 'available_for_open_exchange',
        type: TableColumnTypes.boolean,
        editablePropertyName: 'availableForOpenExchange',
        mutation: UPDATE_UNIT_OPEN_EXCHANGE_AVAILABILITY,
        editableClassName: type,
        action,
        getExtraValidation,
        canAffectAdDeliveryWhenModified: true,
      }),
      availableForStoreFront: new EntityProperty({
        label: capitalize(i18n.global.t('messages.availableForStoreFront')),
        property: 'available_for_storefront',
        type: TableColumnTypes.boolean,
        editablePropertyName: 'availableForStoreFront',
        mutation: UPDATE_UNIT_STORE_FRONT_AVIALABILTY,
        editableClassName: type,
        action,
        getExtraValidation,
        canAffectAdDeliveryWhenModified: true,
      }),
      createdOn: new EntityProperty({
        label: capitalize(i18n.global.t('messages.createdOn')),
        property: 'created_on_utc',
        type: TableColumnTypes.datetime,
      }),
      modifiedOn: new EntityProperty({
        label: capitalize(i18n.global.t('messages.lastModifiedOn')),
        property: 'modified_on_utc',
        type: TableColumnTypes.datetime,
      }),
      spotLength: new EntityProperty({
        label: capitalize(i18n.global.t('messages.defaultAdDuration')),
        property: 'spot_length',
        type: TableColumnTypes.integer,
        editablePropertyName: 'spotLength',
        mutation: UPDATE_UNIT_SPOTLENGTH,
        editableClassName: type,
        action,
        getExtraValidation,
        canAffectAdDeliveryWhenModified: true,
      }),
      minSpotLength: new EntityProperty({
        label: capitalize(i18n.global.t('messages.minAdDuration')),
        property: 'min_spot_length',
        type: TableColumnTypes.integer,
        editablePropertyName: 'minSpotLength',
        mutation: UPDATE_UNIT_MIN_SPOTLENGTH,
        editableClassName: type,
        action,
        getExtraValidation,
        canAffectAdDeliveryWhenModified: true,
      }),
      maxSpotLength: new EntityProperty({
        label: capitalize(i18n.global.t('messages.maxAdDuration')),
        property: 'max_spot_length',
        type: TableColumnTypes.integer,
        editablePropertyName: 'maxSpotLength',
        mutation: UPDATE_UNIT_MAX_SPOTLENGTH,
        editableClassName: type,
        action,
        getExtraValidation,
        canAffectAdDeliveryWhenModified: true,
      }),
      appDomainID: new EntityProperty({
        label: capitalize(i18n.global.t('names.appDomain', { n: 1 })),
        property: 'app_domain_id',
        type: TableColumnTypes.string,
      }),
      allowHtml: new EntityProperty({
        label: capitalize(i18n.global.t('messages.allowHtml')),
        property: 'allow_html',
        type: TableColumnTypes.boolean,
        editablePropertyName: 'allowHtml',
        mutation: UPDATE_UNIT_ALLOWHTML,
        editableClassName: type,
        action,
        getExtraValidation,
        canAffectAdDeliveryWhenModified: true,
      }),
      allowImage: new EntityProperty({
        label: capitalize(i18n.global.t('messages.allowImages')),
        property: 'allow_image',
        type: TableColumnTypes.boolean,
        editablePropertyName: 'allowImage',
        mutation: UPDATE_UNIT_ALLOWIMAGE,
        editableClassName: type,
        action,
        getExtraValidation,
        canAffectAdDeliveryWhenModified: true,
      }),
      allowVideo: new EntityProperty({
        label: capitalize(i18n.global.t('messages.allowVideos')),
        property: 'allow_video',
        type: TableColumnTypes.boolean,
        editablePropertyName: 'allowVideo',
        mutation: UPDATE_UNIT_ALLOWVIDEO,
        editableClassName: type,
        action,
        getExtraValidation,
      }),
      allowZip: new EntityProperty({
        label: capitalize(i18n.global.t('messages.allowZip')),
        property: 'allow_zip',
        type: TableColumnTypes.boolean,
        editablePropertyName: 'allowZip',
        mutation: UPDATE_UNIT_ALLOWZIP,
        editableClassName: type,
        action,
        getExtraValidation,
        canAffectAdDeliveryWhenModified: true,
      }),
      allowAudio: new EntityProperty({
        label: capitalize(i18n.global.t('messages.allowAudioOnlyFiles')),
        property: 'allow_audio',
        type: TableColumnTypes.boolean,
        editablePropertyName: 'allowAudio',
        mutation: UPDATE_UNIT_ALLOWAUDIO,
        editableClassName: type,
        action,
        getExtraValidation,
        canAffectAdDeliveryWhenModified: true,
      }),
      facingDirection: new EntityProperty({
        label: capitalize(i18n.global.t('messages.facingDirection')),
        property: 'facing_direction',
        type: TableColumnTypes.string,
        editablePropertyName: 'facingDirection',
        mutation: UPDATE_UNIT_FACING_DIRECTION,
        editableClassName: type,
        bulkCreateOrderIndex: 30,
        source: () => UnitFacingDirectionOptions.options(),
        sourceID: 'value',
        sourceLabel: 'name',
        action,
        getExtraValidation,
        canAffectAdDeliveryWhenModified: true,
      }),
      languages: new EntityProperty({
        label: capitalize(i18n.global.t('messages.languages')),
        property: 'languagesDescription',
        type: TableColumnTypes.string,
        editablePropertyName: 'unitLanguages',
        mutation: UPDATE_UNIT_UNITLANGUAGES,
        editableClassName: type,
        source: () => runQueryInScope(useLanguages),
        sourceFilterProperty: 'unit_languages',
        editablePropertyAcceptsMultipleValues: true,
        preMutationTransform: (ids, entity) => {
          const languages = peekQueryInScope(useLanguages);
          return ids.map((id) => {
            const language = languages.find((x) => x.id === id);
            return new UnitLanguage(-1, language?.id, entity?.id);
          });
        },
        postMutationCallback: (value, entity) => {
          // Need to update the unitID prop in UnitLanguage because it was not accessible when the object was created in 'preMutationTransform'.
          entity.unitLanguages.forEach((ul) => {
            ul.unitID = entity.id;
          });
        },
        /**
         * HV-9617 - The TableCellEditor component calls this action with an array of language IDs.
         * Need to convert the language IDs to UnitLanguage objects before sending to the API.
         */
        action: (unit) => {
          const languages = peekQueryInScope(useLanguages);
          const unit_languages = unit.unit_languages.map((id) => {
            const language = languages.find((x) => x.id === id);
            return new UnitLanguage(-1, language?.id, unit?.id).toApi();
          });
          return action({ ...unit, unit_languages });
        },
        getExtraValidation,
        canAffectAdDeliveryWhenModified: true,
        entityValueGetterTransform: (value: Array<UnitLanguage>) => value.map((x) => x.language),
        areValuesEqual: (from: Unit, to: Unit) => {
          if (from == null && to == null) return true;
          return areArraysEqual(
            from.unitLanguages.map((x) => x.languageID),
            to.unitLanguages.map((x) => x.languageID),
          );
        },
      }),
      minSecondsBetweenAdDomainPlays: new EntityProperty({
        label: capitalize(i18n.global.t('messages.minSecondsBetweenAdDomainPlays')),
        property: 'min_seconds_between_ad_domain_plays',
        type: TableColumnTypes.integer,
        editablePropertyName: 'minSecondsBetweenAdDomainPlays',
        mutation: UPDATE_UNIT_MIN_SECONDS_BETWEEN_AD_DOMAIN_PLAYS,
        editableClassName: type,
        action,
        getExtraValidation,
        canAffectAdDeliveryWhenModified: true,
        defaultValue: 0,
      }),
      physicalScreenWidth: new EntityProperty({
        label: capitalize(i18n.global.t('messages.physicalScreenWidth')),
        property: 'physicalScreenWidth',
        type: TableColumnTypes.integer,
        toStringOverride: (value) =>
          value.physicalScreenWidth
            ? `${value.physicalScreenWidth} cm (${Math.round(value.physicalScreenWidth / 2.54)} in)`
            : DASH,
      }),
      physicalScreenHeight: new EntityProperty({
        label: capitalize(i18n.global.t('messages.physicalScreenHeight')),
        property: 'physicalScreenHeight',
        type: TableColumnTypes.integer,
        toStringOverride: (value) =>
          value.physicalScreenHeight
            ? `${value.physicalScreenHeight} cm (${Math.round(value.physicalScreenHeight / 2.54)} in)`
            : DASH,
      }),
      timezone: new EntityProperty({
        label: capitalize(i18n.global.t('messages.timezone')),
        property: 'timezone',
        type: TableColumnTypes.string,
        source: timeZonesNames.map((x) => ({ id: x, name: x })),
        editablePropertyName: 'timezone',
        mutation: UPDATE_UNIT_TIMEZONE,
        editableClassName: type,
        action,
        getExtraValidation,
      }),
      iabBlockCategories: new EntityProperty({
        label: capitalize(i18n.global.t('names.iabCategory', { n: 2 })),
        property: 'iabBlockCategoriesDescription',
        type: TableColumnTypes.string,
        source: IABCategories.hierarchicalOptions(),
        sourceFilterProperty: 'iab_blacklist_categories',
        sourceGroupValues: 'subCategories',
        sourceGroupLabel: 'name',
        areSourceGroupParentsSelectable: true,
        editablePropertyName: 'iabBlockCategories',
        mutation: UPDATE_UNIT_IAB_BLOCK_CATEGORIES,
        editableClassName: type,
        editablePropertyAcceptsMultipleValues: true,
        preMutationTransform: (ids) => ids.map((id) => IABCategories.options().find((o) => o.id === id)),
        action,
        getExtraValidation,
        canAffectAdDeliveryWhenModified: true,
      }),
      impressionVerifierVendor: new EntityProperty({
        label: capitalize(i18n.global.t('names.impressionVerifierVendor', { n: 1 })),
        property: 'impression_verifier_vendor',
        type: TableColumnTypes.string,
        source: Object.values(ImpressionVerifierVendorEnum).map((v) => ({ id: v, name: v })),
        editablePropertyName: 'impressionVerifierVendor',
        mutation: UPDATE_UNIT_IMPRESSION_VERIFIER_VENDOR,
        editableClassName: type,
        postMutationCallback: (value, entity) => {
          entity.impressionVerifierVendor = value?.id ?? value;
        },
        action,
        getExtraValidation,
        canAffectAdDeliveryWhenModified: true,
      }),
      impressionVerifierVendorExternalId: new EntityProperty({
        label: capitalize(i18n.global.t('names.impressionVerifierVendorExternalId', { n: 1 })),
        property: 'impression_verifier_vendor_external_id',
        type: TableColumnTypes.string,
        editablePropertyName: 'impressionVerifierVendorExternalId',
        mutation: UPDATE_UNIT_IMPRESSION_VERIFIER_VENDOR_EXTERNAL_ID,
        preMutationTransform: (text) => (text?.trim() === '' ? null : text),
        editableClassName: type,
        action,
        getExtraValidation,
        canAffectAdDeliveryWhenModified: true,
      }),
      enableStrictIABCategoriesBlocking: new EntityProperty({
        label: capitalize(i18n.global.t('messages.strictIABCategoryBlocking')),
        property: 'enable_strict_iab_blacklisting',
        type: TableColumnTypes.boolean,
        editablePropertyName: 'enableStrictIABCategoriesBlocking',
        mutation: UPDATE_UNIT_ENABLE_STRICT_IAB_BLOCK_CATEGORIES,
        editableClassName: type,
        action,
        getExtraValidation,
        canAffectAdDeliveryWhenModified: true,
        defaultValue: { label: capitalize(i18n.global.t('actions.yes')), value: false },
      }),
      enableStrictIABFrequencyCapping: new EntityProperty({
        label: capitalize(i18n.global.t('messages.strictIABFrequencyCapping')),
        property: 'enable_strict_iab_frequency_capping',
        type: TableColumnTypes.boolean,
        editablePropertyName: 'enableStrictIABFrequencyCapping',
        mutation: UPDATE_UNIT_ENABLE_STRICT_FREQUENCY_CAPPING,
        editableClassName: type,
        action,
        getExtraValidation,
        canAffectAdDeliveryWhenModified: true,
        defaultValue: { label: capitalize(i18n.global.t('actions.yes')), value: false },
      }),
      minSecondsBetweenIABCategoryPlays: new EntityProperty({
        label: capitalize(i18n.global.t('messages.iabCategoryFrequencyCap', { n: 1 })),
        property: 'min_seconds_between_iab_category_plays',
        type: TableColumnTypes.integer,
        editablePropertyName: 'minSecondsBetweenIABCategoryPlays',
        mutation: UPDATE_UNIT_MIN_SECONDS_BETWEEN_IAB_CATEGORY_PLAYS,
        editableClassName: type,
        action,
        getExtraValidation,
        canAffectAdDeliveryWhenModified: true,
        defaultValue: 0,
      }),
      operatingHoursDescriptionString: new EntityProperty({
        label: capitalize(i18n.global.t('names.operatingHour', { n: 2 })),
        property: 'operating_hours',
        type: TableColumnTypes.string,
        editablePropertyName: 'operatingHoursRanges',
        validationPropertyName: 'operatingHours',
        mutation: UPDATE_UNIT_OPERATINGHOURS,
        editableClassName: type,
        preMutationTransform: (text) => {
          try {
            const result = parseRanges(text);
            return convertRangesToDaypart(result.ranges);
          } catch (e) {
            return InvalidDaypart;
          }
        },
        action,
        getExtraValidation,
        canAffectAdDeliveryWhenModified: true,
        defaultValue: {
          label: daypartToRanges(FullDaypart),
          value: FullDaypart,
        },
      }),
      blockUniqueAdvertiserIDs: new EntityProperty({
        label: capitalize(i18n.global.t('names.blockedAdvertiser')),
        property: 'blockUniqueAdvertisersDescription',
        type: TableColumnTypes.string,
        source: () => rootStore.dispatch('orgApp/getAvailableUniqueAdvertisers'),
        sourceFilterProperty: 'blacklist_unique_advertiser_ids',
        editablePropertyName: 'blockUniqueAdvertiserIDs',
        mutation: UPDATE_UNIT_BLOCKUNIQUEADVERTISERIDS,
        editableClassName: type,
        editablePropertyAcceptsMultipleValues: true,
        action,
        getExtraValidation,
        canAffectAdDeliveryWhenModified: true,
      }),
      fallbackCreatives: new EntityProperty({
        label: capitalize(i18n.global.t('names.fallbackCreative')),
        property: 'unitfallbackcreatives',
        type: TableColumnTypes.custom,
        editablePropertyName: 'fallbackCreatives',
        editableClassName: type,
        mutation: UPDATE_UNIT_FALLBACK_CREATIVES,
        sourceLabel: 'displayName',
        sourceID: 'id',
      }),
      physicalUnitCount: new EntityProperty({
        label: capitalize(i18n.global.t('names.overrideScreenCount', { n: 1 })),
        property: 'physical_unit_count',
        type: TableColumnTypes.integer,
        editablePropertyName: 'physicalUnitCount',
        mutation: UPDATE_UNIT_PHYSICAL_UNIT_COUNT,
        editableClassName: type,
        action,
        getExtraValidation,
      }),
      customFields: new EntityProperty({
        areValuesEqual: (from, to) => {
          return (
            areArraysEqual(
              from.properties.map((x) => x.id),
              to.properties.map((x) => x.id),
            ) &&
            areArraysEqual(
              from.properties.map((x) => x.value),
              to.properties.map((x) => x.value),
            )
          );
        },
        label: capitalize(i18n.global.t('names.customField', { n: 2 })),
        property: 'customFields',
        type: TableColumnTypes.list,
        editablePropertyAcceptsMultipleValues: true,
        editablePropertyName: 'customFields',
        editableClassName: type,
        action,
        getExtraValidation,
      }),
    };

    return Unit.entityPropertiesCached;
  }

  static get entityName(): string {
    return type;
  }

  static async getEntityIDsFromModels(models): Promise<{ model: Unit; id: number }[]> {
    if (models.length === 0) return [];

    try {
      const externalIDs = models.map((x) => x.externalID);
      const getUnitsAction: Action<IPaginationResult<Unit>> = ({
        queryParams,
        showLoader,
      }: {
        queryParams?: QueryParams;
        showLoader: boolean;
      }) => rootStore.dispatch('orgApp/getUnits', { queryParams, showLoader });
      const qp = QueryParams.all(FilterCollection.for('external_id', externalIDs, FilterComparisonOperator.IN));
      qp.include = ['unit_id', 'external_id'];
      const response = await qp.run(getUnitsAction);
      const entities = response.items as Unit[];
      return models.map((model) => ({ model, id: entities.find((x) => x.externalID === model.externalID)?.id }));
    } catch (e) {
      throw new Exception('Failed to get entity IDs.');
    }
  }

  static async assignIDs(entities) {
    if (entities.length === 0) return [];

    const existingIDMapping = await Unit.getEntityIDsFromModels(entities);
    entities.forEach((x) => {
      x.id = existingIDMapping.find((y) => y.model === x)?.id || -1;
    });
    return entities;
  }

  static async patchEntities(entities) {
    try {
      const payload = this.getPatchPayload(entities);
      await rootStore.dispatch('orgApp/patchUnits', payload);
    } catch (e) {
      throw new Exception('Failed to patch units.');
    }
  }

  static async getEntities(ids) {
    if (ids.length === 0) return [];

    try {
      const queryParams = QueryParams.all(FilterCollection.for('unit_id', ids, FilterComparisonOperator.IN));
      queryParams.expand = Unit.expands;
      queryParams.showLoader = false;
      const response = await rootStore.dispatch('orgApp/getUnits', {
        queryParams,
        cancelTokenSource: queryParams.cancelTokenSource,
        showLoader: false,
      });
      return response.items;
    } catch (e) {
      throw new Exception('Failed to get units.');
    }
  }
}
